/**
 * Project shell sections (handoff §4 "Project shell", spec §2 "Project
 * navigation"). The URL is the state: `?tab=<section>` picks the section and
 * `?view=<sub-view>` picks a view inside it. Section values are the canonical
 * `tab` values; every pre-redesign `tab` value maps onto one of them.
 */

export interface Section { tab: string; label: string }

// Order and wording follow the founder concept's project tab bar
// (docs/founder-redesign-concept-2026-09-20.html, renderProject).
export const SECTIONS: Section[] = [
    { tab: 'Overview', label: 'Overview' },
    { tab: 'Tasks', label: 'Tasks' },
    { tab: 'Planning', label: 'Planning' },
    { tab: 'Activity', label: 'Activity' },
    { tab: 'Members', label: 'Members & access' },
    { tab: 'Reports', label: 'Reports' },
    { tab: 'Tools', label: 'Project tools' },
];

/** A restricted (partial-access) user sees only these two, and Tasks reads "Shared tasks". */
export const RESTRICTED_SECTIONS: Section[] = [
    { tab: 'Overview', label: 'Overview' },
    { tab: 'Tasks', label: 'Shared tasks' },
];

/** Heading row above a section's sub-view switcher (concept filterbar / section-label). */
export const SECTION_HEADS: Record<string, { title: string; note?: string }> = {
    Planning: { title: 'Plan the delivery', note: 'Milestones, task dates, and dependencies stay within the project.' },
    Reports: { title: 'Progress you can act on', note: 'Keep analytics, burndown, and exportable status reports together.' },
    Tools: { title: 'Useful project capabilities, organized together' },
};

/** Project tools cards (concept featureCard): one per Tools sub-view. */
export const TOOL_CARDS: Record<string, { title: string; description: string }> = {
    ai: { title: 'Project AI assistance', description: 'Planning, questions, briefings, and dependency analysis.' },
    automations: { title: 'Automation rules', description: 'Configured triggers, filters, notifications, and task actions.' },
    time: { title: 'Time & recurring work', description: 'Your running timer; time is logged on each task.' },
    settings: { title: 'Project settings', description: 'Name, status, repository, colour, and dates.' },
    data: { title: 'Data & lifecycle', description: 'Archive, restore deleted tasks, or delete the project.' },
};

/** Sub-views per section; the first one is the default. */
export const VIEWS: Record<string, { view: string; label: string }[]> = {
    Tasks: [{ view: 'list', label: 'List' }, { view: 'board', label: 'Board' }],
    Planning: [{ view: 'plan', label: 'Plan' }, { view: 'timeline', label: 'Timeline' }],
    Reports: [{ view: 'analytics', label: 'Analytics' }, { view: 'report', label: 'Status report' }],
    Tools: [
        { view: 'ai', label: 'AI Co-Pilot' }, { view: 'automations', label: 'Automations' }, { view: 'time', label: 'Time' },
        { view: 'settings', label: 'Settings' }, { view: 'data', label: 'Data & lifecycle' },
    ],
};

/** Pre-redesign `?tab=` values that are not section values themselves. */
export const LEGACY_TABS: Record<string, { tab: string; view?: string }> = {
    'Board': { tab: 'Tasks', view: 'board' },
    'Timeline': { tab: 'Planning', view: 'timeline' },
    'Plan': { tab: 'Planning', view: 'plan' },
    'Analytics': { tab: 'Reports', view: 'analytics' },
    'Report': { tab: 'Reports', view: 'report' },
    'Time Machine': { tab: 'Activity' },
    'AI Co-Pilot': { tab: 'Tools', view: 'ai' },
    'Automations': { tab: 'Tools', view: 'automations' },
};

/**
 * The section and view to render. An unknown tab falls back to Overview and an
 * unknown view to the section's default. A restricted user gets exactly what
 * they got before the redesign — Overview and the Tasks list. Any Tasks view is
 * the list for them (never the Board); every other section clamps to Overview.
 */
export function resolveSection(rawTab: string | null, rawView: string | null, restricted: boolean): { tab: string; view?: string } {
    const tab = SECTIONS.some(s => s.tab === rawTab) ? rawTab as string : 'Overview';
    const views = VIEWS[tab];
    const view = views ? (views.find(v => v.view === rawView) ?? views[0]).view : undefined;
    if (restricted) return tab === 'Tasks' ? { tab, view: 'list' } : { tab: 'Overview' };
    return { tab, view };
}
